import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { useAuth } from './AuthContext'
import { supabase } from '../lib/supabase'

interface FeedbackContextType {
  soundEnabled: boolean
  loading: boolean
  setSoundEnabled: (enabled: boolean) => void
}

const FeedbackContext = createContext<FeedbackContextType | undefined>(undefined)

export function FeedbackProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const [soundEnabled, setSoundEnabled] = useState(true)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!user) {
      setLoading(false)
      return
    }

    // Initial load
    loadSettings()

    // Subscribe to realtime changes
    const channel = supabase
      .channel(`feedback-settings-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'user_profiles',
          filter: `id=eq.${user.id}`
        },
        (payload) => {
          if (payload.new && 'sound_enabled' in payload.new) {
            setSoundEnabled(payload.new.sound_enabled ?? true)
          }
        }
      )
      .subscribe()

    // Cleanup subscription on unmount
    return () => {
      supabase.removeChannel(channel)
    }
  }, [user])

  const loadSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('sound_enabled')
        .eq('id', user?.id)
        .single()

      if (error) throw error

      if (data) {
        setSoundEnabled(data.sound_enabled ?? true)
      }
    } catch (error) {
      console.error('Error loading feedback settings:', error)
      // Use defaults on error
      setSoundEnabled(true)
    } finally {
      setLoading(false)
    }
  }

  return (
    <FeedbackContext.Provider value={{ soundEnabled, loading, setSoundEnabled }}>
      {children}
    </FeedbackContext.Provider>
  )
}

export function useFeedbackContext() {
  const context = useContext(FeedbackContext)
  if (context === undefined) {
    throw new Error('useFeedbackContext must be used within a FeedbackProvider')
  }
  return context
}
